import React, { useEffect, useRef, useState } from 'react';

import Badge from '@adsk/alloy-react-badge';
import { Box, Flex, Text } from '@adsk/alloy-react-box';
import { displayDate } from '@adsk/alloy-react-datetime';
import Divider from '@adsk/alloy-react-divider';
import { useOnlyLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { MultipleAssigneeDisplay } from './index';
import { SIZES } from './MultipleAssigneeDisplay';
import { REVIEW_STATE, IN_PROGRESS_STEP } from '../../consts';
import { useStatus } from '../../hooks';
import { useTranslation } from '../../i18n';
import { SubmittalsItem } from '../../types';
import { getDiffFromToday } from '../../utils/date';
import { getStepNumber } from '../../utils/steps';

interface SubmittalItemDisplayProps {
  item: SubmittalsItem;
}

const SubmittalsItemDisplay: React.FC<SubmittalItemDisplayProps> = ({
  item,
}) => {
  const { t } = useTranslation();
  const { locale } = useOnlyLocale();
  const {
    title,
    dueDate,
    ballInCourtUsers,
    stateId,
    customIdentifierHumanReadable,
  } = item;
  const isItemInReview = stateId === REVIEW_STATE;
  const totalSteps = item.steps.length;
  const inProgressStep = item.steps.find((step) => {
    if (step.status === IN_PROGRESS_STEP) return step.stepNumber;
    return 0;
  });
  const currentStep = inProgressStep?.stepNumber || 0;
  const { stateText, statusText, color } = useStatus(stateId);
  const reviewStateText = ` ${stateText} | ${t(
    'STEP',
  )} ${getStepNumber(currentStep)}/${getStepNumber(totalSteps)}`;

  const subStatusText =
    isItemInReview && totalSteps > 0 ? reviewStateText : stateText;
  const [isStatusOverflowing, setStatusOverflowing] = useState(false);
  const statusContainer = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // The font size for the text is 24px and there is a 4px margin from the top
    // If the text overflows it spans 2 lines and thus has height greater than 28
    if (statusContainer.current && statusContainer.current.clientHeight > 28) {
      setStatusOverflowing(true);
    } else {
      setStatusOverflowing(false);
    }
  }, [statusContainer.current?.clientHeight]);

  const dateToDisplay = dueDate && displayDate(dueDate, { locale });

  const statusBadge = (
    <Badge
      style={{ paddingLeft: 0, paddingRight: 0 }}
      size={Badge.SIZES.SMALL}
      variant={Badge.VARIANTS.LINE}
      color={color}
      stacked={false}
      subStatus={subStatusText}
    >
      {statusText}
    </Badge>
  );
  const dueDateDisplay = dueDate && (
    <Text
      style={{
        color: getDiffFromToday(dueDate) < 0 ? theme.colors.red700 : undefined,
      }}
    >
      {t('DUE', { date: dateToDisplay })}
    </Text>
  );

  return (
    <Flex
      flexDirection="column"
      justifyContent="space-between"
      marginRight="4px"
    >
      <header
        style={{
          display: '-webkit-box',
          overflow: 'hidden',
          WebkitBoxOrient: 'vertical',
          ...theme.typography.bodyMediumBold,
        }}
      >
        <OverflowTooltip
          singleLine={false}
          autoTipOnEllipsis={true}
          content={title}
        >
          <Text marginRight="8px">{`#${customIdentifierHumanReadable}`}</Text>
          <span>{title}</span>
        </OverflowTooltip>
      </header>
      <Box marginTop="4px">
        <MultipleAssigneeDisplay
          userIds={[...ballInCourtUsers]}
          size={SIZES.SMALL}
          itemData={item}
        />
      </Box>
      <Flex
        ref={statusContainer}
        marginTop="4px"
        flexDirection={isStatusOverflowing ? 'column' : 'row'}
      >
        {statusBadge}
        {dueDate && !isStatusOverflowing && (
          <Divider
            style={{
              alignSelf: 'center',
              margin: '0 12px',
            }}
            variant={Divider.VARIANTS.VERTICAL}
            height={12}
            color={theme.colors.charcoal400}
          />
        )}
        <Box
          typography="bodySmall"
          marginTop={isStatusOverflowing ? '4px' : 0}
          alignSelf={isStatusOverflowing ? 'start' : 'center'}
        >
          {dueDateDisplay}
        </Box>
      </Flex>
    </Flex>
  );
};

SubmittalsItemDisplay.displayName = 'SubmittalsItemDisplay';

export default SubmittalsItemDisplay;
